import { makeStyles } from '@material-ui/styles'
import Progress from 'core/components/progress/Progress'
import { ErrorMessage } from 'core/components/validatedForm/ErrorMessage'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import Info from 'core/components/validatedForm/Info'
import RadioFields from 'core/components/validatedForm/radio-fields'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import Theme from 'core/themes/model'
import moment from 'moment'
import { getCurrentUser } from 'openstack/helpers'
import { lazy, Suspense, useCallback, useEffect, useState } from 'react'
import 'react-calendar/dist/Calendar.css'
import 'react-clock/dist/Clock.css'
import 'react-datetime-picker/dist/DateTimePicker.css'
import { formatTableDate } from 'utils/misc'
import { hasOtherUserVm } from '../helpers'
import { createLease, updateLease } from './actions'

const DateTimePicker = lazy(() => import('react-datetime-picker'))

const useStyles = makeStyles((theme: Theme) => ({
  input: {
    '& .react-datetime-picker__wrapper': {
      padding: 8,
    },
  },
}))

const options = [
  { label: 'Delete VM', value: 'delete' },
  { label: 'Power Off VM', value: 'power off' },
]

export default function CustomVmLeaseModal({ rows: vms, onClose }) {
  const vm = vms?.[0]
  const leasePolicy = vm?.leasePolicy
  const leaseDays = leasePolicy ? Math.floor(leasePolicy.expiry_mins / 1440) : 0
  const leaseHours = leasePolicy ? Math.floor((leasePolicy.expiry_mins - leaseDays * 1440) / 60) : 0
  const leaseMinutes = leasePolicy
    ? leasePolicy.expiry_mins - leaseDays * 1440 - leaseHours * 60
    : 0

  const defaultParams = {
    date: null,
    action: 'power off',
    // action: vm?.customLease
    //   ? vm.customLease.action
    //   : vm?.leasePolicy
    //   ? vm.leasePolicy.action
    //   : 'power off',
  }

  const { params, getParamsUpdater, updateParams, setParams } = useParams<{
    date: any
    action: string
  }>(defaultParams)
  const classes = useStyles()
  const [dateError, setError] = useState('')

  const { update: createFn, updating, error, reset } = useUpdateAction(createLease)
  const {
    update: updateFn,
    updating: updatingLease,
    error: updateError,
    reset: resetUpdate,
  } = useUpdateAction(updateLease)

  const submitForm = useCallback(async () => {
    const expiration = moment(params.date)
    const utcExpiration = expiration.utc().format('YYYY-MM-DDTHH:mm:ss') + 'Z'
    const body = {
      instance_uuid: vm.id,
      expiry: utcExpiration,
      action: params.action,
    }
    const operation = vm?.customLease ? updateFn : createFn
    const { success } = await operation({
      tenantId: vm.tenant_id,
      vmId: vm.id,
      body,
    })
    if (success) {
      handleClose(true)
    }
  }, [vm, leasePolicy, params])

  const handleClose = (success?: boolean) => {
    setParams(defaultParams)
    reset()
    resetUpdate()
    onClose(success)
  }

  const { role: userRole, userId } = getCurrentUser()

  useEffect(() => {
    const expiry = vm?.customLease
      ? vm.customLease.expiry
      : leasePolicy
      ? moment(vm?.created)
          .add(leasePolicy.expiry_mins, 'minutes')
          .format('YYYY-MM-DDTHH:mm:ss')
      : null
    const action = vm?.customLease?.action || vm?.leasePolicy?.action || 'power off'
    updateParams({ date: expiry, action })
  }, [vm, leasePolicy])

  return (
    <ModalForm
      open
      title={`Customize Lease`}
      onSubmit={submitForm}
      onClose={handleClose}
      submitting={updating || updatingLease}
      error={error || updateError}
      submitTitle={`Update Lease`}
      disableSubmit={!params.date}
      loading={!vm}
    >
      {userRole === 'member' && hasOtherUserVm({ vms, userId }) && (
        <Info error>NOTE: You are performing an operation on another user's VM.</Info>
      )}
      <br />
      <FormFieldSection title="Lease Configuration">
        <Text variant="body2">
          You can override the tenant-wide lease for this individual VM here. If the VM’s current
          lease is still active, you can edit the lease end to an earlier date and time.
          Alternatively, you can extend the lease up to a maximum of one additional lease period, as
          defined in the tenant’s lease policy (for example, an extra 5 days if the tenant policy is
          a 5-day lease).
        </Text>
        <RadioFields
          id="action"
          title="Action Upon Lease Expiration"
          options={options}
          value={params.action}
          onChange={getParamsUpdater('action')}
        />
        <Text variant="body2">
          This VM is currently set to expire at <b>{formatTableDate(vm.expirationTime)}</b>.
        </Text>
        <Text variant="body2">
          You can extend this VM's lease policy, which expires in{' '}
          <b>
            {leaseDays} days, {leaseHours} hours, and {leaseMinutes} minutes
          </b>{' '}
          from the current time.
        </Text>
        <Suspense fallback={<Progress loading />}>
          <DateTimePicker
            className={classes.input}
            onChange={getParamsUpdater('date')}
            value={params.date}
          />
        </Suspense>
        {dateError && <ErrorMessage>{dateError}</ErrorMessage>}
      </FormFieldSection>
    </ModalForm>
  )
}
